import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState, VisibilityState } from '@tanstack/react-table'
import { Button } from '@trellis/roots/button'
import {
  BulkActionBar,
  ColumnVisibilityMenu,
  DataTable,
  RowActionsMenu,
} from '@trellis/roots/data'
import type { RowAction } from '@trellis/roots/data'
import { DateRangePicker } from '@trellis/roots/date-range-picker'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@trellis/roots/dropdown-menu'
import { SearchInput } from '@trellis/roots/forms'
import { PageHeader, TabBar } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { StatusBadge } from '@trellis/roots/status'
import { BulkTagPicker } from '@trellis/roots/tags'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import type {
  ExpenseCategory,
  ExpenseResponse,
  RecurringExpenseResponse,
} from '@trellis/shared'
import { EXPENSE_CATEGORY_META, FEATURE, formatRecurrence } from '@trellis/shared'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Tag,
  Trash2,
  Upload,
  XCircle,
  Zap,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { toast } from 'sonner'

import { ReceiptBundleButton } from '@/components/reports'
import { FilterSheet } from '@/components/shell'
import {
  useActivateRecurringExpenseMutation,
  useAssignExpenseTagsMutation,
  useClaimExpenseMutation,
  useBillExpenseMutation,
  useBulkDelete,
  useBulkTagApply,
  useCancelRecurringExpenseMutation,
  useClientsSummaryQuery,
  useColumnVisibility,
  useDebouncedSearch,
  useDeleteExpenseMutation,
  useDeleteRecurringExpenseMutation,
  useDisclosure,
  useDuplicateExpenseMutation,
  useExpensesQuery,
  usePauseRecurringExpenseMutation,
  usePinnedColumn,
  useRecurringExpensesQuery,
  useRecurringExpenseStatsQuery,
  useRemoveExpenseTagMutation,
  useTableSorting,
  useTagsQuery,
  useUnclaimExpenseMutation,
  useTriggerRecurringExpenseMutation,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import { formatCurrency, formatDate } from '@/lib/format'
import { exportExpenses } from '@/requests/data-export'
import type { ExpenseListParams } from '@/requests/expenses'

import {
  BillExpenseDialog,
  ExpenseRowActionsMenu,
  ExpenseSummarySheet,
} from './(components)'
import {
  CLAIMED_BADGE_LABELS,
  CLAIMED_BADGE_STYLES,
  EXPENSE_STATUS_STYLES,
} from './constants'

type ExpenseTab = 'ALL' | 'BUSINESS' | 'REIMBURSEMENT' | 'ARCHIVED'

type RecurringRow = { _kind: 'recurring'; data: RecurringExpenseResponse }
type ExpenseRow = { _kind: 'expense'; data: ExpenseResponse }
type TableRow = RecurringRow | ExpenseRow

const expensesRouteApi = getRouteApi('/_authenticated/_dashboard/expenses/')

export const ALL_EXPENSE_VIEWS = ['expenses', 'recurring'] as const

export const ALL_EXPENSE_TABS = [
  'ALL',
  'BUSINESS',
  'REIMBURSEMENT',
  'ARCHIVED',
] as const satisfies readonly ExpenseTab[]

export const ALL_EXPENSE_CATEGORIES = Object.keys(EXPENSE_CATEGORY_META) as [
  ExpenseCategory,
  ...ExpenseCategory[],
]

interface ExpensesBusinessSearchParams {
  businessCategory?: ExpenseCategory
  businessFrom?: string
  businessTo?: string
}

interface ExpensesReimbursementSearchParams {
  reimbursementClientId?: string
  reimbursementFrom?: string
  reimbursementTo?: string
}

/**
 * These three build functions turn the expenses route's validated search into the exact
 * options shapes passed to {@link useExpensesQuery} here and to
 * `expensesInfiniteQueryOptions` in the route's loader — keeping both on the same query
 * keys so the loader's prefetch is actually hit. `q` (search text) is intentionally
 * excluded from all three: it's local component state, not URL-synced. The page always
 * runs all three queries regardless of the active tab, so the loader prefetches all three
 * too.
 */
export function buildExpensesBusinessQueryOptions(
  search: ExpensesBusinessSearchParams,
): ExpenseListParams {
  const dateComplete =
    search.businessFrom !== undefined && search.businessTo !== undefined
  return {
    type: 'BUSINESS',
    limit: 25,
    ...(search.businessCategory ? { category: search.businessCategory } : {}),
    ...(dateComplete
      ? {
          startDate: new Date(search.businessFrom!).toISOString().slice(0, 10),
          endDate: new Date(search.businessTo!).toISOString().slice(0, 10),
        }
      : {}),
  }
}

export function buildExpensesReimbursementQueryOptions(
  search: ExpensesReimbursementSearchParams,
): ExpenseListParams {
  const dateComplete =
    search.reimbursementFrom !== undefined && search.reimbursementTo !== undefined
  return {
    type: 'REIMBURSEMENT',
    limit: 25,
    ...(search.reimbursementClientId ? { clientId: search.reimbursementClientId } : {}),
    ...(dateComplete
      ? {
          startDate: new Date(search.reimbursementFrom!).toISOString().slice(0, 10),
          endDate: new Date(search.reimbursementTo!).toISOString().slice(0, 10),
        }
      : {}),
  }
}

export function buildExpensesAllQueryOptions(): ExpenseListParams {
  return { limit: 25 }
}

const REXP_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

const CATEGORY_OPTIONS = (
  Object.entries(EXPENSE_CATEGORY_META) as [
    ExpenseCategory,
    { label: string; code: number },
  ][]
).sort((a, b) => a[1].label.localeCompare(b[1].label))

function formatClaimable(claimableAmount: number, claimableAmountType: string): string {
  if (claimableAmountType === 'PERCENTAGE') return `${claimableAmount}%`
  return formatCurrency(claimableAmount)
}

const BUSINESS_COLUMN_SPECS = [
  { id: 'expenseNumber', label: 'Expense #' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'category', label: 'Category' },
  { id: 'description', label: 'Description' },
  { id: 'date', label: 'Date' },
  { id: 'totalCost', label: 'Total Cost' },
  { id: 'claimableAmount', label: 'Claimable' },
  { id: 'status', label: 'Status' },
]

const REIMBURSEMENT_COLUMN_SPECS = [
  { id: 'expenseNumber', label: 'Expense #' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'client', label: 'Client' },
  { id: 'description', label: 'Description' },
  { id: 'date', label: 'Date' },
  { id: 'totalCost', label: 'Total Cost' },
  { id: 'claimableAmount', label: 'Claimable' },
  { id: 'status', label: 'Status' },
]

const ALL_COLUMN_SPECS = [
  { id: 'expenseNumber', label: 'Expense #' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'categoryOrClient', label: 'Category / Client' },
  { id: 'description', label: 'Description' },
  { id: 'date', label: 'Date' },
  { id: 'totalCost', label: 'Total Cost' },
  { id: 'claimableAmount', label: 'Claimable' },
  { id: 'status', label: 'Status' },
]

const DEFAULT_BUSINESS_COLUMN_VISIBILITY: VisibilityState = {
  category: false,
  description: false,
}

const DEFAULT_REIMBURSEMENT_COLUMN_VISIBILITY: VisibilityState = {
  description: false,
}

const DEFAULT_ALL_COLUMN_VISIBILITY: VisibilityState = {
  description: false,
}

interface RecurringExpenseActions {
  onActivate: (recurringExpense: RecurringExpenseResponse) => void
  onPause: (recurringExpense: RecurringExpenseResponse) => void
  onTrigger: (recurringExpense: RecurringExpenseResponse) => void
  onCancelClick: (recurringExpense: RecurringExpenseResponse) => void
  onDeleteClick: (recurringExpense: RecurringExpenseResponse) => void
}

function renderRecurringExpenseActions(
  recurringExpense: RecurringExpenseResponse,
  actions: RecurringExpenseActions,
) {
  const rowActions: RowAction[] = [
    {
      label: 'Edit',
      icon: <Edit className="mr-2 h-4 w-4" />,
      href: `/expenses/recurring/${recurringExpense.id}/edit`,
    } satisfies RowAction,
    ...(recurringExpense.status === 'PAUSED'
      ? [
          {
            label: 'Activate',
            icon: <Play className="mr-2 h-4 w-4" />,
            onClick: () => actions.onActivate(recurringExpense),
            separator: true,
          } satisfies RowAction,
        ]
      : []),
    ...(recurringExpense.status === 'ACTIVE'
      ? [
          {
            label: 'Trigger now',
            icon: <Zap className="mr-2 h-4 w-4" />,
            onClick: () => actions.onTrigger(recurringExpense),
            separator: true,
          } satisfies RowAction,
          {
            label: 'Pause',
            icon: <Pause className="mr-2 h-4 w-4" />,
            onClick: () => actions.onPause(recurringExpense),
          } satisfies RowAction,
        ]
      : []),
    ...(recurringExpense.status === 'ACTIVE' || recurringExpense.status === 'PAUSED'
      ? [
          {
            label: 'Cancel',
            icon: <XCircle className="mr-2 h-4 w-4" />,
            onClick: () => actions.onCancelClick(recurringExpense),
            destructive: true,
          } satisfies RowAction,
        ]
      : []),
  ]
  return (
    <RowActionsMenu
      triggerLabel="Open recurring expense actions"
      actions={rowActions}
      linkComponent={Link}
    />
  )
}

function buildRecurringExpenseColumns(
  recurringExpenseActions: RecurringExpenseActions,
): ColumnDef<RecurringExpenseResponse>[] {
  return [
    {
      id: 'rexpNumber',
      header: 'Expense #',
      accessorFn: (row) => row.rexpNumber,
      cell: ({ row }) => (
        <span className="text-sm font-medium tabular-nums">
          {`REXP-${String(row.original.rexpNumber).padStart(4, '0')}`}
        </span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Title',
      meta: { truncate: true },
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.type === 'BUSINESS' ? 'Business' : 'Reimbursement'}
        </span>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const cat = row.original.category
        if (!cat) return <span className="text-muted-foreground">—</span>
        return <span className="text-sm">{EXPENSE_CATEGORY_META[cat]?.label ?? cat}</span>
      },
    },
    {
      id: 'frequency',
      header: 'Frequency',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatRecurrence({
            frequency: row.original.frequency,
            interval: row.original.interval,
            daysOfWeek: row.original.daysOfWeek,
            dayOfMonth: row.original.dayOfMonth ?? undefined,
            monthOfYear: row.original.monthOfYear ?? undefined,
          })}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          labels={REXP_STATUS_LABELS}
          styles={EXPENSE_STATUS_STYLES}
        />
      ),
    },
    {
      accessorKey: 'nextRunAt',
      header: 'Next run',
      cell: ({ row }) =>
        row.original.nextRunAt ? (
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatDate(row.original.nextRunAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) =>
        renderRecurringExpenseActions(row.original, recurringExpenseActions),
    },
  ]
}

function buildBusinessColumns(
  onExpenseDeleteClick: (expense: ExpenseResponse) => void,
  onSummaryClick: (expense: ExpenseResponse) => void,
  recurringExpenseActions: RecurringExpenseActions,
  onClaimClick: (expense: ExpenseResponse) => void,
  onUnclaimClick: (expense: ExpenseResponse) => void,
  onDuplicateClick: (expense: ExpenseResponse) => void,
): ColumnDef<TableRow>[] {
  return [
    {
      id: 'expenseNumber',
      header: 'Expense #',
      accessorFn: (row) =>
        row._kind === 'recurring' ? '' : (row.data.expenseNumber ?? ''),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const { expenseNumber, recurringExpenseId } = row.original.data
        return (
          <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums">
            {expenseNumber}
            {recurringExpenseId && (
              <Tooltip touch="none">
                <TooltipTrigger asChild>
                  <Link
                    to="/expenses/recurring/$id"
                    params={{ id: recurringExpenseId }}
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    <span className="bg-muted text-muted-foreground inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium">
                      ↻ REXP
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Generated from recurring expense</TooltipContent>
              </Tooltip>
            )}
          </span>
        )
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: () => <span className="text-muted-foreground text-sm">Business</span>,
    },
    {
      id: 'title',
      header: 'Title',
      accessorFn: (row) =>
        row._kind === 'recurring' ? row.data.title : (row.data.title ?? ''),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return <span className="font-medium">{row.original.data.title}</span>
        }
        const title = row.original.data.title
        if (!title) return <span className="text-muted-foreground">—</span>
        return <span className="font-medium">{title}</span>
      },
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          const cat = row.original.data.category
          if (!cat) return <span className="text-muted-foreground">—</span>
          return (
            <span className="text-sm">{EXPENSE_CATEGORY_META[cat]?.label ?? cat}</span>
          )
        }
        const cat = row.original.data.category
        if (!cat) return <span className="text-muted-foreground">—</span>
        const meta = EXPENSE_CATEGORY_META[cat]
        return (
          <span className="text-sm" data-code={meta.code}>
            {meta.label}
          </span>
        )
      },
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return (
            <span className="text-muted-foreground text-sm">
              {formatRecurrence({
                frequency: row.original.data.frequency,
                interval: row.original.data.interval,
                daysOfWeek: row.original.data.daysOfWeek,
                dayOfMonth: row.original.data.dayOfMonth ?? undefined,
                monthOfYear: row.original.data.monthOfYear ?? undefined,
              })}
            </span>
          )
        }
        const desc = row.original.data.description
        if (!desc) return <span className="text-muted-foreground">—</span>
        const text = desc.replace(/<[^>]+>/g, '').trim()
        return (
          <span className="text-muted-foreground line-clamp-1 max-w-[200px] text-sm">
            {text || '—'}
          </span>
        )
      },
    },
    {
      id: 'date',
      header: 'Date',
      accessorFn: (row) =>
        row._kind === 'recurring' ? (row.data.nextRunAt ?? '') : row.data.expenseDate,
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          const { nextRunAt, occurrenceCount, maxOccurrences } = row.original.data
          return (
            <div className="space-y-0.5">
              <span className="flex items-center gap-1.5 text-sm tabular-nums">
                <RefreshCw className="text-primary size-3.5 shrink-0" />
                <span className="text-muted-foreground">
                  {nextRunAt ? formatDate(nextRunAt) : '—'}
                </span>
              </span>
              {maxOccurrences !== null && (
                <span className="text-muted-foreground pl-5 text-xs">
                  {occurrenceCount}/{maxOccurrences} runs
                </span>
              )}
            </div>
          )
        }
        return (
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatDate(row.original.data.expenseDate)}
          </span>
        )
      },
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorFn: (row) => row.data.totalCost,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.data.totalCost)}
        </span>
      ),
    },
    {
      id: 'claimableAmount',
      header: 'Claimable',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatClaimable(
            row.original.data.claimableAmount,
            row.original.data.claimableAmountType,
          )}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) =>
        row._kind === 'recurring' ? row.data.status : row.data.claimed ? 'CLAIMED' : '',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return (
            <StatusBadge
              status={row.original.data.status}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        }
        return row.original.data.claimed ? (
          <StatusBadge
            status="CLAIMED"
            styles={CLAIMED_BADGE_STYLES}
            labels={CLAIMED_BADGE_LABELS}
          />
        ) : null
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return renderRecurringExpenseActions(row.original.data, recurringExpenseActions)
        }
        return (
          <ExpenseRowActionsMenu
            expense={row.original.data}
            onDeleteClick={onExpenseDeleteClick}
            onPreviewClick={onSummaryClick}
            onDuplicateClick={onDuplicateClick}
            onClaimClick={onClaimClick}
            onUnclaimClick={onUnclaimClick}
          />
        )
      },
    },
  ]
}

function buildReimbursementColumns(
  onExpenseDeleteClick: (expense: ExpenseResponse) => void,
  onSummaryClick: (expense: ExpenseResponse) => void,
  onBillClick: (expense: ExpenseResponse) => void,
  recurringExpenseActions: RecurringExpenseActions,
  onDuplicateClick: (expense: ExpenseResponse) => void,
): ColumnDef<TableRow>[] {
  return [
    {
      id: 'expenseNumber',
      header: 'Expense #',
      accessorFn: (row) =>
        row._kind === 'recurring' ? '' : (row.data.expenseNumber ?? ''),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const { expenseNumber, recurringExpenseId } = row.original.data
        return (
          <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums">
            {expenseNumber}
            {recurringExpenseId && (
              <Tooltip touch="none">
                <TooltipTrigger asChild>
                  <Link
                    to="/expenses/recurring/$id"
                    params={{ id: recurringExpenseId }}
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    <span className="bg-muted text-muted-foreground inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium">
                      ↻ REXP
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Generated from recurring expense</TooltipContent>
              </Tooltip>
            )}
          </span>
        )
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: () => <span className="text-muted-foreground text-sm">Reimbursement</span>,
    },
    {
      id: 'title',
      header: 'Title',
      accessorFn: (row) =>
        row._kind === 'recurring' ? row.data.title : (row.data.title ?? ''),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return <span className="font-medium">{row.original.data.title}</span>
        }
        const title = row.original.data.title
        if (!title) return <span className="text-muted-foreground">—</span>
        return <span className="font-medium">{title}</span>
      },
    },
    {
      id: 'client',
      header: 'Client',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.data.clientName ?? '—'}</span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return (
            <span className="text-muted-foreground text-sm">
              {formatRecurrence({
                frequency: row.original.data.frequency,
                interval: row.original.data.interval,
                daysOfWeek: row.original.data.daysOfWeek,
                dayOfMonth: row.original.data.dayOfMonth ?? undefined,
                monthOfYear: row.original.data.monthOfYear ?? undefined,
              })}
            </span>
          )
        }
        const desc = row.original.data.description
        const text = desc ? desc.replace(/<[^>]+>/g, '').trim() : null
        return (
          <span className="text-muted-foreground line-clamp-1 max-w-[200px] text-sm">
            {text || '—'}
          </span>
        )
      },
    },
    {
      id: 'date',
      header: 'Date',
      accessorFn: (row) =>
        row._kind === 'recurring' ? (row.data.nextRunAt ?? '') : row.data.expenseDate,
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          const { nextRunAt, occurrenceCount, maxOccurrences } = row.original.data
          return (
            <div className="space-y-0.5">
              <span className="flex items-center gap-1.5 text-sm tabular-nums">
                <RefreshCw className="text-primary size-3.5 shrink-0" />
                <span className="text-muted-foreground">
                  {nextRunAt ? formatDate(nextRunAt) : '—'}
                </span>
              </span>
              {maxOccurrences !== null && (
                <span className="text-muted-foreground pl-5 text-xs">
                  {occurrenceCount}/{maxOccurrences} runs
                </span>
              )}
            </div>
          )
        }
        return (
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatDate(row.original.data.expenseDate)}
          </span>
        )
      },
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorFn: (row) => row.data.totalCost,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.data.totalCost)}
        </span>
      ),
    },
    {
      id: 'claimableAmount',
      header: 'Claimable',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatClaimable(
            row.original.data.claimableAmount,
            row.original.data.claimableAmountType,
          )}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) =>
        row._kind === 'recurring'
          ? row.data.status
          : row.data.billed
            ? 'INVOICED'
            : row.data.invoiceLineItemId
              ? 'PENDING'
              : '',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return (
            <StatusBadge
              status={row.original.data.status}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        }
        const { billed, invoiceLineItemId } = row.original.data
        if (billed)
          return (
            <StatusBadge
              status="INVOICED"
              labels={{ INVOICED: 'Billed' }}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        if (invoiceLineItemId)
          return (
            <StatusBadge
              status="PENDING"
              labels={{ PENDING: 'On invoice' }}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        return null
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') {
          return renderRecurringExpenseActions(row.original.data, recurringExpenseActions)
        }
        return (
          <ExpenseRowActionsMenu
            expense={row.original.data}
            onDeleteClick={onExpenseDeleteClick}
            onPreviewClick={onSummaryClick}
            onDuplicateClick={onDuplicateClick}
            onBillClick={onBillClick}
          />
        )
      },
    },
  ]
}

function buildAllColumns(
  onExpenseDeleteClick: (expense: ExpenseResponse) => void,
  onSummaryClick: (expense: ExpenseResponse) => void,
  onBillClick: (expense: ExpenseResponse) => void,
  onClaimClick: (expense: ExpenseResponse) => void,
  onUnclaimClick: (expense: ExpenseResponse) => void,
  onDuplicateClick: (expense: ExpenseResponse) => void,
): ColumnDef<TableRow>[] {
  return [
    {
      id: 'expenseNumber',
      header: 'Expense #',
      accessorFn: (row) =>
        row._kind === 'recurring' ? '' : (row.data.expenseNumber ?? ''),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const { expenseNumber, recurringExpenseId } = row.original.data
        return (
          <span className="flex flex-col gap-1.5 text-sm font-medium tabular-nums">
            {expenseNumber}
            {recurringExpenseId && (
              <Tooltip touch="none">
                <TooltipTrigger asChild>
                  <Link
                    to="/expenses/recurring/$id"
                    params={{ id: recurringExpenseId }}
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    <span className="bg-muted text-muted-foreground inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium">
                      ↻ REXP
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Generated from recurring expense</TooltipContent>
              </Tooltip>
            )}
          </span>
        )
      },
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (row) => row.data.type,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.data.type === 'BUSINESS' ? 'Business' : 'Reimbursement'}
        </span>
      ),
    },
    {
      id: 'title',
      header: 'Title',
      accessorFn: (row) =>
        row._kind === 'recurring' ? row.data.title : (row.data.title ?? ''),
      cell: ({ row }) => {
        const title = row.original.data.title
        if (!title) return <span className="text-muted-foreground">—</span>
        return <span className="font-medium">{title}</span>
      },
    },
    {
      id: 'categoryOrClient',
      header: 'Category / Client',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const { type, category, clientName } = row.original.data
        if (type === 'REIMBURSEMENT') {
          return <span className="font-medium">{clientName ?? '—'}</span>
        }
        if (!category) return <span className="text-muted-foreground">—</span>
        return (
          <span className="text-sm">
            {EXPENSE_CATEGORY_META[category]?.label ?? category}
          </span>
        )
      },
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const desc = row.original.data.description
        const text = desc ? desc.replace(/<[^>]+>/g, '').trim() : null
        return (
          <span className="text-muted-foreground line-clamp-1 max-w-[200px] text-sm">
            {text || '—'}
          </span>
        )
      },
    },
    {
      id: 'date',
      header: 'Date',
      accessorFn: (row) => (row._kind === 'recurring' ? '' : row.data.expenseDate),
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        return (
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatDate(row.original.data.expenseDate)}
          </span>
        )
      },
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorFn: (row) => row.data.totalCost,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.data.totalCost)}
        </span>
      ),
    },
    {
      id: 'claimableAmount',
      header: 'Claimable',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatClaimable(
            row.original.data.claimableAmount,
            row.original.data.claimableAmountType,
          )}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        const { type, claimed, billed, invoiceLineItemId } = row.original.data
        if (type === 'BUSINESS') {
          return claimed ? (
            <StatusBadge
              status="CLAIMED"
              styles={CLAIMED_BADGE_STYLES}
              labels={CLAIMED_BADGE_LABELS}
            />
          ) : null
        }
        if (billed)
          return (
            <StatusBadge
              status="INVOICED"
              labels={{ INVOICED: 'Billed' }}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        if (invoiceLineItemId)
          return (
            <StatusBadge
              status="PENDING"
              labels={{ PENDING: 'On invoice' }}
              styles={EXPENSE_STATUS_STYLES}
            />
          )
        return null
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original._kind === 'recurring') return null
        return (
          <ExpenseRowActionsMenu
            expense={row.original.data}
            onDeleteClick={onExpenseDeleteClick}
            onPreviewClick={onSummaryClick}
            onDuplicateClick={onDuplicateClick}
            onClaimClick={onClaimClick}
            onUnclaimClick={onUnclaimClick}
            onBillClick={onBillClick}
          />
        )
      },
    },
  ]
}

function buildArchivedColumns(
  onDeleteClick: (recurringExpense: RecurringExpenseResponse) => void,
): ColumnDef<RecurringExpenseResponse>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Title',
      meta: { truncate: true },
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.type === 'BUSINESS' ? 'Business' : 'Reimbursement'}
        </span>
      ),
    },
    {
      id: 'frequency',
      header: 'Frequency',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatRecurrence({
            frequency: row.original.frequency,
            interval: row.original.interval,
            daysOfWeek: row.original.daysOfWeek,
            dayOfMonth: row.original.dayOfMonth ?? undefined,
            monthOfYear: row.original.monthOfYear ?? undefined,
          })}
        </span>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.totalCost)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const recurringExpense = row.original
        const rowActions: RowAction[] = [
          {
            label: 'Delete',
            icon: <Trash2 className="mr-2 h-4 w-4" />,
            onClick: () => onDeleteClick(recurringExpense),
            destructive: true,
          } satisfies RowAction,
        ]
        return (
          <RowActionsMenu
            triggerLabel="Open archived recurring expense actions"
            actions={rowActions}
            linkComponent={Link}
          />
        )
      },
    },
  ]
}

export function ExpensesPage() {
  useSetBreadcrumbs([{ label: 'Expenses' }])

  const navigate = useNavigate()
  const navigateSearch = expensesRouteApi.useNavigate()
  const {
    view,
    tab: activeTab,
    businessFrom: businessFromParam,
    businessTo: businessToParam,
    businessCategory,
    reimbursementFrom: reimbursementFromParam,
    reimbursementTo: reimbursementToParam,
    reimbursementClientId,
  } = expensesRouteApi.useSearch()
  const businessFrom = businessFromParam ? new Date(businessFromParam) : undefined
  const businessTo = businessToParam ? new Date(businessToParam) : undefined
  const reimbursementFrom = reimbursementFromParam
    ? new Date(reimbursementFromParam)
    : undefined
  const reimbursementTo = reimbursementToParam
    ? new Date(reimbursementToParam)
    : undefined
  const {
    columnVisibility: businessColumnVisibility,
    onColumnVisibilityChange: onBusinessColumnVisibilityChange,
  } = useColumnVisibility('expenses-business-columns', DEFAULT_BUSINESS_COLUMN_VISIBILITY)
  const {
    columnVisibility: reimbursementColumnVisibility,
    onColumnVisibilityChange: onReimbursementColumnVisibilityChange,
  } = useColumnVisibility(
    'expenses-reimbursement-columns',
    DEFAULT_REIMBURSEMENT_COLUMN_VISIBILITY,
  )
  const {
    columnVisibility: allColumnVisibility,
    onColumnVisibilityChange: onAllColumnVisibilityChange,
  } = useColumnVisibility('expenses-all-columns', DEFAULT_ALL_COLUMN_VISIBILITY)
  const businessSorting = useTableSorting('expenses-business-sorting')
  const reimbursementSorting = useTableSorting('expenses-reimbursement-sorting')
  const allSorting = useTableSorting('expenses-all-sorting')
  const businessPinned = usePinnedColumn('expenses-business-pinned-column')
  const reimbursementPinned = usePinnedColumn('expenses-reimbursement-pinned-column')
  const allPinned = usePinnedColumn('expenses-all-pinned-column')
  const expenseToDelete = useDisclosure<ExpenseResponse>()
  const summaryExpense = useDisclosure<ExpenseResponse>()
  const billExpense = useDisclosure<ExpenseResponse>()
  const recurringExpenseToCancel = useDisclosure<RecurringExpenseResponse>()
  const recurringExpenseToDelete = useDisclosure<RecurringExpenseResponse>()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const {
    inputValue: searchInput,
    debouncedValue: searchQuery,
    setInputValue: setSearchInput,
  } = useDebouncedSearch()

  function handleViewChange(nextView: (typeof ALL_EXPENSE_VIEWS)[number]) {
    void navigateSearch({ search: (prev) => ({ ...prev, view: nextView }) })
  }

  function handleTabChange(tab: ExpenseTab) {
    void navigateSearch({ search: (prev) => ({ ...prev, tab }) })
    clearExpenseSelection()
  }

  function updateBusinessFilters(patch: {
    businessFrom?: string | undefined
    businessTo?: string | undefined
    businessCategory?: ExpenseCategory | undefined
  }) {
    void navigateSearch({ search: (prev) => ({ ...prev, ...patch }) })
  }

  function updateReimbursementFilters(patch: {
    reimbursementFrom?: string | undefined
    reimbursementTo?: string | undefined
    reimbursementClientId?: string | undefined
  }) {
    void navigateSearch({ search: (prev) => ({ ...prev, ...patch }) })
  }

  const businessDateRange: DateRange | undefined =
    businessFrom || businessTo ? { from: businessFrom, to: businessTo } : undefined

  const reimbursementDateRange: DateRange | undefined =
    reimbursementFrom || reimbursementTo
      ? { from: reimbursementFrom, to: reimbursementTo }
      : undefined

  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const allTagsQuery = useTagsQuery()
  const allTags = allTagsQuery.data ?? []

  const isRecurringView = view === 'recurring'

  const { data: clientsResult } = useClientsSummaryQuery()
  const clients = clientsResult?.data ?? []

  const businessQuery = useExpensesQuery({
    ...buildExpensesBusinessQueryOptions({
      businessCategory,
      businessFrom: businessFromParam,
      businessTo: businessToParam,
    }),
    ...(searchQuery ? { q: searchQuery } : {}),
  })

  const reimbursementQuery = useExpensesQuery({
    ...buildExpensesReimbursementQueryOptions({
      reimbursementClientId,
      reimbursementFrom: reimbursementFromParam,
      reimbursementTo: reimbursementToParam,
    }),
    ...(searchQuery ? { q: searchQuery } : {}),
  })

  const allQuery = useExpensesQuery({
    ...buildExpensesAllQueryOptions(),
    ...(searchQuery ? { q: searchQuery } : {}),
  })

  const recurringQuery = useRecurringExpensesQuery({})
  const archivedQuery = useRecurringExpensesQuery(
    activeTab === 'ARCHIVED' ? { status: 'CANCELLED' } : undefined,
  )

  const { data: statsResult } = useRecurringExpenseStatsQuery(isRecurringView)

  const activeRecurringExpenses = (recurringQuery.data?.data ?? []).filter(
    (recurringExpense) => recurringExpense.status !== 'CANCELLED',
  )

  const businessExpenseRows = businessQuery.currentPageData?.data ?? []
  const reimbursementExpenseRows = reimbursementQuery.currentPageData?.data ?? []
  const allExpenseRows = allQuery.currentPageData?.data ?? []

  const businessRows = useMemo<TableRow[]>(
    () =>
      businessExpenseRows.map(
        (expense): TableRow => ({ _kind: 'expense', data: expense }),
      ),
    [businessExpenseRows],
  )
  const reimbursementRows = useMemo<TableRow[]>(
    () =>
      reimbursementExpenseRows.map(
        (expense): TableRow => ({ _kind: 'expense', data: expense }),
      ),
    [reimbursementExpenseRows],
  )
  const allRows = useMemo<TableRow[]>(
    () =>
      allExpenseRows.map((expense): TableRow => ({ _kind: 'expense', data: expense })),
    [allExpenseRows],
  )

  const archivedRecurringExpenses = archivedQuery.data?.data ?? []

  const deleteMutation = useDeleteExpenseMutation()
  const duplicateMutation = useDuplicateExpenseMutation()
  const billMutation = useBillExpenseMutation()
  const claimMutation = useClaimExpenseMutation()
  const unclaimMutation = useUnclaimExpenseMutation()
  const activateMutation = useActivateRecurringExpenseMutation()
  const pauseMutation = usePauseRecurringExpenseMutation()
  const cancelMutation = useCancelRecurringExpenseMutation()
  const triggerMutation = useTriggerRecurringExpenseMutation()
  const deleteRecurringExpenseMutation = useDeleteRecurringExpenseMutation()
  const assignExpenseTagsMutation = useAssignExpenseTagsMutation()
  const removeExpenseTagMutation = useRemoveExpenseTagMutation()

  async function handleConfirmDelete() {
    if (!expenseToDelete.value) return
    const targetId = expenseToDelete.value.id
    expenseToDelete.close()
    try {
      await deleteMutation.mutateAsync(targetId)
      toast.success('Expense deleted')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Failed to delete expense'
      toast.error('Cannot delete expense', { description: message })
    }
  }

  function handleDuplicateExpense(expense: ExpenseResponse) {
    duplicateMutation.mutate(expense.id, {
      onSuccess: () => {
        toast.success('Expense duplicated')
      },
      onError: (caughtError) => {
        toast.error('Failed to duplicate expense', {
          description: caughtError instanceof Error ? caughtError.message : undefined,
        })
      },
    })
  }

  async function handleConfirmBill(invoiceId: string) {
    if (!billExpense.value) return
    const targetId = billExpense.value.id
    billExpense.close()
    try {
      await billMutation.mutateAsync({ expenseId: targetId, invoiceId })
      toast.success('Expense added to invoice')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Failed to add expense to invoice'
      toast.error('Could not add to invoice', { description: message })
    }
  }

  async function handleClaimExpense(expense: ExpenseResponse) {
    try {
      await claimMutation.mutateAsync(expense.id)
      toast.success('Expense marked as claimed')
    } catch (caughtError) {
      toast.error('Failed to update expense', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleUnclaimExpense(expense: ExpenseResponse) {
    try {
      await unclaimMutation.mutateAsync(expense.id)
      toast.success('Expense unmarked as claimed')
    } catch (caughtError) {
      toast.error('Failed to update expense', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleBulkClaim() {
    const results = await Promise.allSettled(
      selectedExpenses.map((expense) => claimMutation.mutateAsync(expense.id)),
    )
    const failed = results.filter((result) => result.status === 'rejected').length
    const succeeded = results.length - failed
    if (failed > 0) {
      toast.error('Some claims failed', {
        description: `${succeeded} claimed, ${failed} failed.`,
      })
    } else {
      toast.success(`${succeeded} expense${succeeded === 1 ? '' : 's'} marked as claimed`)
    }
    clearExpenseSelection()
  }

  async function handleBulkUnclaim() {
    const results = await Promise.allSettled(
      selectedExpenses.map((expense) => unclaimMutation.mutateAsync(expense.id)),
    )
    const failed = results.filter((result) => result.status === 'rejected').length
    const succeeded = results.length - failed
    if (failed > 0) {
      toast.error('Some updates failed', {
        description: `${succeeded} unclaimed, ${failed} failed.`,
      })
    } else {
      toast.success(
        `${succeeded} expense${succeeded === 1 ? '' : 's'} unmarked as claimed`,
      )
    }
    clearExpenseSelection()
  }

  async function handleActivateRecurringExpense(
    recurringExpense: RecurringExpenseResponse,
  ) {
    try {
      await activateMutation.mutateAsync(recurringExpense.id)
      toast.success('Recurring expense activated')
    } catch (caughtError) {
      toast.error('Failed to activate', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handlePauseRecurringExpense(recurringExpense: RecurringExpenseResponse) {
    try {
      await pauseMutation.mutateAsync(recurringExpense.id)
      toast.success('Recurring expense paused')
    } catch (caughtError) {
      toast.error('Failed to pause', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleTriggerRecurringExpense(
    recurringExpense: RecurringExpenseResponse,
  ) {
    try {
      await triggerMutation.mutateAsync(recurringExpense.id)
      toast.success('Run triggered — expense generated')
    } catch (caughtError) {
      toast.error('Failed to trigger run', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleConfirmCancelRecurringExpense() {
    if (!recurringExpenseToCancel.value) return
    const target = recurringExpenseToCancel.value
    recurringExpenseToCancel.close()
    try {
      await cancelMutation.mutateAsync(target.id)
      toast.success('Recurring expense cancelled')
    } catch (caughtError) {
      toast.error('Failed to cancel', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleConfirmDeleteRecurringExpense() {
    if (!recurringExpenseToDelete.value) return
    const target = recurringExpenseToDelete.value
    recurringExpenseToDelete.close()
    try {
      await deleteRecurringExpenseMutation.mutateAsync(target.id)
      toast.success('Recurring expense deleted')
    } catch (caughtError) {
      toast.error('Failed to delete', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  const recurringExpenseActions: RecurringExpenseActions = {
    onActivate: (recurringExpense) =>
      void handleActivateRecurringExpense(recurringExpense),
    onPause: (recurringExpense) => void handlePauseRecurringExpense(recurringExpense),
    onTrigger: (recurringExpense) => void handleTriggerRecurringExpense(recurringExpense),
    onCancelClick: recurringExpenseToCancel.open,
    onDeleteClick: recurringExpenseToDelete.open,
  }

  const recurringExpenseColumns = buildRecurringExpenseColumns(recurringExpenseActions)
  const businessColumns = buildBusinessColumns(
    expenseToDelete.open,
    summaryExpense.open,
    recurringExpenseActions,
    (expense) => void handleClaimExpense(expense),
    (expense) => void handleUnclaimExpense(expense),
    handleDuplicateExpense,
  )
  const reimbursementColumns = buildReimbursementColumns(
    expenseToDelete.open,
    summaryExpense.open,
    billExpense.open,
    recurringExpenseActions,
    handleDuplicateExpense,
  )
  const allColumns = buildAllColumns(
    expenseToDelete.open,
    summaryExpense.open,
    billExpense.open,
    (expense) => void handleClaimExpense(expense),
    (expense) => void handleUnclaimExpense(expense),
    handleDuplicateExpense,
  )
  const archivedColumns = buildArchivedColumns(recurringExpenseToDelete.open)

  const isBusinessTab = activeTab === 'BUSINESS'
  const isArchivedTab = activeTab === 'ARCHIVED'
  const isAllTab = activeTab === 'ALL'
  const activeSorting = isAllTab
    ? allSorting
    : isBusinessTab
      ? businessSorting
      : reimbursementSorting
  const activePinned = isAllTab
    ? allPinned
    : isBusinessTab
      ? businessPinned
      : reimbursementPinned

  const selectedExpenses = (
    isAllTab
      ? allExpenseRows
      : isBusinessTab
        ? businessExpenseRows
        : reimbursementExpenseRows
  ).filter((expense) => rowSelection[expense.id])

  function clearExpenseSelection() {
    setRowSelection({})
  }

  const {
    open: bulkDeleteOpen,
    setOpen: setBulkDeleteOpen,
    handleBulkDelete,
  } = useBulkDelete({
    selected: selectedExpenses,
    getId: (expense) => expense.id,
    deleteFn: (id) => deleteMutation.mutateAsync(id),
    label: 'expense',
    onSuccess: clearExpenseSelection,
  })
  const {
    open: bulkTagPickerOpen,
    setOpen: setBulkTagPickerOpen,
    handleBulkTagApply,
    allSelectedTagIds,
    someSelectedTagIds,
  } = useBulkTagApply({
    selected: selectedExpenses,
    getId: (expense) => expense.id,
    getTags: (expense) => expense.tags ?? [],
    assignFn: (id, tagIds) =>
      assignExpenseTagsMutation.mutateAsync({ expenseId: id, tagIds }),
    removeFn: (id, tagId) =>
      removeExpenseTagMutation.mutateAsync({ expenseId: id, tagId }),
    invalidateKey: ['expenses'],
    onSuccess: clearExpenseSelection,
  })

  const activeExpenseQuery = isAllTab
    ? allQuery
    : isBusinessTab
      ? businessQuery
      : reimbursementQuery
  const activeRows = isAllTab ? allRows : isBusinessTab ? businessRows : reimbursementRows

  return (
    <div className="p-8">
      <PageHeader
        title="Expenses"
        subtitle="Track business deductions and client reimbursements."
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="More options"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() =>
                    exportExpenses(
                      isAllTab ? undefined : isBusinessTab ? 'BUSINESS' : 'REIMBURSEMENT',
                    )
                  }
                >
                  <Download className="mr-2 size-4" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/expenses/import">
                    <Upload className="mr-2 size-4" />
                    Import CSV
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <ReceiptBundleButton />

            <Button asChild>
              <Link to="/expenses/new">
                <Plus className="size-4" />
                New Expense
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-6">
        <TabBar
          tabs={[
            {
              key: 'expenses',
              label: 'Expenses',
              count: businessExpenseRows.length + reimbursementExpenseRows.length,
            },
            {
              key: 'recurring',
              label: 'Recurring',
              count: activeRecurringExpenses.length,
            },
          ]}
          activeKey={view}
          onChange={(key) => handleViewChange(key as (typeof ALL_EXPENSE_VIEWS)[number])}
        />
      </div>

      {isRecurringView ? (
        <div>
          {statsResult?.data && (
            <div className="bg-muted/30 text-muted-foreground mb-4 rounded-lg border px-4 py-3 text-sm">
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.total}
              </span>{' '}
              Schedules ·{' '}
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.active}
              </span>{' '}
              Active ·{' '}
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.paused}
              </span>{' '}
              Paused
            </div>
          )}

          <DataTable
            columns={recurringExpenseColumns}
            data={activeRecurringExpenses}
            isLoading={recurringQuery.isFetching && activeRecurringExpenses.length === 0}
            emptyMessage="No recurring expenses."
            onRowClick={(recurringExpense) =>
              void navigate({
                to: '/expenses/recurring/$id',
                params: { id: recurringExpense.id },
              })
            }
          />
        </div>
      ) : (
        <>
          {selectedExpenses.length > 0 ? (
            <BulkActionBar
              selectedCount={selectedExpenses.length}
              actions={[
                ...(isTagsEnabled
                  ? [
                      {
                        label: 'Assign tags',
                        icon: <Tag className="size-4" />,
                        onClick: () => setBulkTagPickerOpen(true),
                      },
                    ]
                  : []),
                ...(isBusinessTab
                  ? [
                      {
                        label: 'Mark as Claimed',
                        icon: <CheckCircle2 className="size-4" />,
                        onClick: () => void handleBulkClaim(),
                      },
                      {
                        label: 'Unmark as Claimed',
                        icon: <XCircle className="size-4" />,
                        onClick: () => void handleBulkUnclaim(),
                      },
                    ]
                  : []),
                {
                  label: 'Delete',
                  icon: <Trash2 className="size-4" />,
                  onClick: () => setBulkDeleteOpen(true),
                  variant: 'destructive',
                },
              ]}
              onClearSelection={clearExpenseSelection}
              className="mb-3"
            />
          ) : null}
          <div className="mb-3">
            <FilterSheet
              activeCount={
                isArchivedTab || isAllTab
                  ? 0
                  : isBusinessTab
                    ? (businessDateRange ? 1 : 0) + (businessCategory ? 1 : 0)
                    : (reimbursementDateRange ? 1 : 0) + (reimbursementClientId ? 1 : 0)
              }
              onClear={
                !isArchivedTab && !isAllTab
                  ? () => {
                      if (isBusinessTab) {
                        updateBusinessFilters({
                          businessFrom: undefined,
                          businessTo: undefined,
                          businessCategory: undefined,
                        })
                      } else {
                        updateReimbursementFilters({
                          reimbursementFrom: undefined,
                          reimbursementTo: undefined,
                          reimbursementClientId: undefined,
                        })
                      }
                    }
                  : undefined
              }
              toolbar={
                <>
                  <Select
                    value={activeTab}
                    onValueChange={(value) => handleTabChange(value as ExpenseTab)}
                  >
                    <SelectTrigger
                      aria-label="Expense type"
                      className="text-muted-foreground h-9 w-[210px] rounded-lg font-normal"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Expenses</SelectItem>
                      <SelectItem value="BUSINESS">Business Expenses</SelectItem>
                      <SelectItem value="REIMBURSEMENT">Reimbursements</SelectItem>
                      <SelectItem value="ARCHIVED">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isArchivedTab && !isAllTab && (
                    <>
                      {isBusinessTab ? (
                        <>
                          <DateRangePicker
                            value={businessDateRange}
                            onChange={(range) =>
                              updateBusinessFilters({
                                businessFrom: range?.from?.toISOString(),
                                businessTo: range?.to?.toISOString(),
                              })
                            }
                          />
                          <Select
                            value={businessCategory ?? ''}
                            onValueChange={(value: string) =>
                              updateBusinessFilters({
                                businessCategory:
                                  value === '__all__'
                                    ? undefined
                                    : (value as ExpenseCategory),
                              })
                            }
                          >
                            <SelectTrigger
                              aria-label="Category"
                              className="h-9 w-[220px]"
                            >
                              <SelectValue placeholder="All categories" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All categories</SelectItem>
                              {CATEGORY_OPTIONS.map(([key, meta]) => (
                                <SelectItem key={key} value={key} data-code={meta.code}>
                                  {meta.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(businessCategory || businessDateRange) && (
                            <button
                              onClick={() =>
                                updateBusinessFilters({
                                  businessFrom: undefined,
                                  businessTo: undefined,
                                  businessCategory: undefined,
                                })
                              }
                              className="text-muted-foreground hover:text-foreground text-[12.5px] underline underline-offset-2 transition-colors"
                            >
                              Clear filters
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <DateRangePicker
                            value={reimbursementDateRange}
                            onChange={(range) =>
                              updateReimbursementFilters({
                                reimbursementFrom: range?.from?.toISOString(),
                                reimbursementTo: range?.to?.toISOString(),
                              })
                            }
                          />
                          <Select
                            value={reimbursementClientId ?? ''}
                            onValueChange={(value: string) =>
                              updateReimbursementFilters({
                                reimbursementClientId:
                                  value === '__all__' ? undefined : value,
                              })
                            }
                          >
                            <SelectTrigger aria-label="Client" className="h-9 w-[200px]">
                              <SelectValue placeholder="All clients" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All clients</SelectItem>
                              {clients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(reimbursementClientId || reimbursementDateRange) && (
                            <button
                              onClick={() =>
                                updateReimbursementFilters({
                                  reimbursementFrom: undefined,
                                  reimbursementTo: undefined,
                                  reimbursementClientId: undefined,
                                })
                              }
                              className="text-muted-foreground hover:text-foreground text-[12.5px] underline underline-offset-2 transition-colors"
                            >
                              Clear filters
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <div className="flex-1" />
                  {!isArchivedTab && (
                    <>
                      <SearchInput
                        value={searchInput}
                        onChange={(changeEvent) =>
                          setSearchInput(changeEvent.target.value)
                        }
                        placeholder="Search expenses…"
                        className="max-w-xs"
                      />
                      <ColumnVisibilityMenu
                        columns={
                          isAllTab
                            ? ALL_COLUMN_SPECS
                            : isBusinessTab
                              ? BUSINESS_COLUMN_SPECS
                              : REIMBURSEMENT_COLUMN_SPECS
                        }
                        columnVisibility={
                          isAllTab
                            ? allColumnVisibility
                            : isBusinessTab
                              ? businessColumnVisibility
                              : reimbursementColumnVisibility
                        }
                        onColumnVisibilityChange={
                          isAllTab
                            ? onAllColumnVisibilityChange
                            : isBusinessTab
                              ? onBusinessColumnVisibilityChange
                              : onReimbursementColumnVisibilityChange
                        }
                      />
                    </>
                  )}
                </>
              }
              sheet={
                <>
                  <Select
                    value={activeTab}
                    onValueChange={(value) => handleTabChange(value as ExpenseTab)}
                  >
                    <SelectTrigger
                      aria-label="Expense type"
                      className="text-muted-foreground h-9 w-full rounded-lg font-normal"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Expenses</SelectItem>
                      <SelectItem value="BUSINESS">Business Expenses</SelectItem>
                      <SelectItem value="REIMBURSEMENT">Reimbursements</SelectItem>
                      <SelectItem value="ARCHIVED">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isArchivedTab && !isAllTab && (
                    <>
                      {isBusinessTab ? (
                        <>
                          <DateRangePicker
                            value={businessDateRange}
                            onChange={(range) =>
                              updateBusinessFilters({
                                businessFrom: range?.from?.toISOString(),
                                businessTo: range?.to?.toISOString(),
                              })
                            }
                          />
                          <Select
                            value={businessCategory ?? ''}
                            onValueChange={(value: string) =>
                              updateBusinessFilters({
                                businessCategory:
                                  value === '__all__'
                                    ? undefined
                                    : (value as ExpenseCategory),
                              })
                            }
                          >
                            <SelectTrigger aria-label="Category" className="h-9 w-full">
                              <SelectValue placeholder="All categories" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All categories</SelectItem>
                              {CATEGORY_OPTIONS.map(([key, meta]) => (
                                <SelectItem key={key} value={key} data-code={meta.code}>
                                  {meta.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      ) : (
                        <>
                          <DateRangePicker
                            value={reimbursementDateRange}
                            onChange={(range) =>
                              updateReimbursementFilters({
                                reimbursementFrom: range?.from?.toISOString(),
                                reimbursementTo: range?.to?.toISOString(),
                              })
                            }
                          />
                          <Select
                            value={reimbursementClientId ?? ''}
                            onValueChange={(value: string) =>
                              updateReimbursementFilters({
                                reimbursementClientId:
                                  value === '__all__' ? undefined : value,
                              })
                            }
                          >
                            <SelectTrigger aria-label="Client" className="h-9 w-full">
                              <SelectValue placeholder="All clients" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All clients</SelectItem>
                              {clients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                    </>
                  )}
                </>
              }
            />
          </div>

          {isArchivedTab ? (
            <DataTable
              columns={archivedColumns}
              data={archivedRecurringExpenses}
              isLoading={
                archivedQuery.isFetching && archivedRecurringExpenses.length === 0
              }
              emptyMessage="No cancelled recurring expenses."
            />
          ) : (
            <>
              <DataTable
                columns={
                  isAllTab
                    ? allColumns
                    : isBusinessTab
                      ? businessColumns
                      : reimbursementColumns
                }
                data={activeRows}
                isLoading={
                  activeExpenseQuery.isFetching &&
                  activeExpenseQuery.currentPageData == null
                }
                emptyMessage={
                  isAllTab
                    ? 'No expenses found.'
                    : isBusinessTab
                      ? 'No business expenses found.'
                      : 'No reimbursements found.'
                }
                className="rounded-b-none"
                columnVisibility={
                  isAllTab
                    ? allColumnVisibility
                    : isBusinessTab
                      ? businessColumnVisibility
                      : reimbursementColumnVisibility
                }
                onColumnVisibilityChange={
                  isAllTab
                    ? onAllColumnVisibilityChange
                    : isBusinessTab
                      ? onBusinessColumnVisibilityChange
                      : onReimbursementColumnVisibilityChange
                }
                enableColumnPinning
                pinnedColumnId={activePinned.pinnedColumnId}
                onPinnedColumnChange={activePinned.onPinnedColumnChange}
                sorting={activeSorting.sorting}
                onSortingChange={activeSorting.onSortingChange}
                onRowClick={(row) => {
                  if (row._kind === 'recurring') {
                    void navigate({
                      to: '/expenses/recurring/$id',
                      params: { id: row.data.id },
                    })
                  } else {
                    void navigate({ to: '/expenses/$id', params: { id: row.data.id } })
                  }
                }}
                enableSelection={true}
                getRowId={(row) => row.data.id}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
              />

              <div className="bg-muted/30 text-muted-foreground mt-0 flex items-center justify-between rounded-b-lg border-x border-b px-4 py-3 text-[12.5px]">
                <span>
                  Page {activeExpenseQuery.pageIndex + 1} of{' '}
                  {activeExpenseQuery.totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={activeExpenseQuery.pageIndex === 0}
                    onClick={activeExpenseQuery.goToPrevPage}
                    className="h-7 text-xs"
                  >
                    <ChevronLeft className="mr-1 size-3.5" />
                    Prev
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={
                      !activeExpenseQuery.hasNextPage || activeExpenseQuery.isFetching
                    }
                    onClick={activeExpenseQuery.goToNextPage}
                    className="h-7 text-xs"
                  >
                    Next
                    <ChevronRight className="ml-1 size-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={expenseToDelete.isOpen}
        onOpenChange={expenseToDelete.onOpenChange}
        title="Delete expense?"
        description="This will permanently delete the expense and remove any attached receipt. This action cannot be undone."
        confirmLabel="Delete expense"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={recurringExpenseToCancel.isOpen}
        onOpenChange={recurringExpenseToCancel.onOpenChange}
        title="Cancel recurring expense?"
        description={
          <>
            Future runs for <strong>{recurringExpenseToCancel.value?.title}</strong> will
            stop. Already-generated expenses are unaffected.
          </>
        }
        confirmLabel="Cancel recurring expense"
        cancelLabel="Keep active"
        isPending={cancelMutation.isPending}
        onConfirm={handleConfirmCancelRecurringExpense}
      />

      <ConfirmDialog
        open={recurringExpenseToDelete.isOpen}
        onOpenChange={recurringExpenseToDelete.onOpenChange}
        title="Delete recurring expense?"
        description={
          <>
            <strong>{recurringExpenseToDelete.value?.title}</strong> will be permanently
            deleted. Already-generated expenses are unaffected.
          </>
        }
        confirmLabel="Delete"
        isPending={deleteRecurringExpenseMutation.isPending}
        onConfirm={handleConfirmDeleteRecurringExpense}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Are you absolutely sure?"
        description={`This will permanently delete ${selectedExpenses.length} expense${selectedExpenses.length === 1 ? '' : 's'}. This action cannot be undone.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={() => void handleBulkDelete()}
      />

      {/* Bulk tag picker */}
      {isTagsEnabled && (
        <BulkTagPicker
          open={bulkTagPickerOpen}
          onOpenChange={setBulkTagPickerOpen}
          allTags={allTags}
          allSelectedTagIds={allSelectedTagIds}
          someSelectedTagIds={someSelectedTagIds}
          onApply={(toAdd, toRemove) => void handleBulkTagApply(toAdd, toRemove)}
          isApplying={
            assignExpenseTagsMutation.isPending || removeExpenseTagMutation.isPending
          }
        />
      )}

      <ExpenseSummarySheet
        expense={summaryExpense.value}
        onOpenChange={summaryExpense.onOpenChange}
      />

      <BillExpenseDialog
        expense={billExpense.value}
        isPending={billMutation.isPending}
        onOpenChange={billExpense.onOpenChange}
        onConfirm={handleConfirmBill}
      />
    </div>
  )
}
